// ./Candidate/CandidateSlice/Interview/InterviewSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addInterview, fetchInterviews, updateInterview } from "./InterviewAPI";

const initialState = {
  data: [],
  pagination: null, // { currentPage, lastPage, perPage, total, from, to }
  lastQuery: null,  // { type, status, perPageCount, page }
  loading: false,
  error: null,
  status: "idle", // idle | loading | succeeded | failed
  lastFetch: null,
  addingInterview: false,
  updatingInterview: false,
};

export const fetchInterviewsAsync = createAsyncThunk(
  "Interview/fetch",
  async (
    { type, status, perPageCount = 15, page = 1 } = {},
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchInterviews({ type, status, perPageCount, page });

      if (!data || !data.response) {
        throw new Error("Invalid response format from server");
      }

      const resp = data.response;

      const list = Array.isArray(resp.data) ? resp.data : [];
      const pagination = {
        currentPage: resp.current_page ?? 1,
        lastPage: resp.last_page ?? 1,
        perPage: resp.per_page ?? perPageCount,
        total: resp.total ?? list.length,
        from: resp.from ?? (list.length ? 1 : 0),
        to: resp.to ?? list.length,
      };

      return {
        data: list,
        pagination,
        query: { type, status, perPageCount, page },
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error("Error fetching interviews:", error);
      return rejectWithValue({
        message: error.message || "Failed to fetch interviews",
        code: error.code || "FETCH_ERROR",
      });
    }
  }
);

export const updateInterviewAsync = createAsyncThunk(
  "Interview/update",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      if (!uuid) {
        throw new Error("Interview UUID is required for update");
      }
      if (!data || Object.keys(data).length === 0) {
        throw new Error("Update data is required");
      }

      const response = await updateInterview(uuid, data);

      if (!response) {
        throw new Error("No response received from server");
      }

      return {
        uuid,
        data: response,
        updatedAt: Date.now(),
      };
    } catch (error) {
      console.error("Error updating interview:", error);
      return rejectWithValue({
        message: error.message || "Failed to update interview",
        code: error.code || "UPDATE_ERROR",
        uuid,
      });
    }
  }
);

export const addInterviewAsync = createAsyncThunk(
  "Interview/add",
  async ({ newInterview }, { rejectWithValue }) => {
    try {
      const requiredFields = [
        "applicant_uuid",
        "interview_date",
        "interview_time",
        "duration",
        "call_type",
      ];
      const missingFields = requiredFields.filter(
        (field) => !newInterview[field]
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }

      if (newInterview.hiring_team_email) {
        const emailRegex =
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailRegex.test(newInterview.hiring_team_email)) {
          throw new Error("Invalid email format");
        }
      }

      const interviewDate = new Date(newInterview.interview_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (interviewDate < today) {
        throw new Error("Interview date cannot be in the past");
      }

      const data = await addInterview(newInterview);

      if (!data) {
        throw new Error("No response received from server");
      }

      return {
        ...data,
        addedAt: Date.now(),
      };
    } catch (error) {
      console.error("Error scheduling interview:", error);
      return rejectWithValue({
        message: error.message || "Failed to schedule interview",
        code: error.code || "ADD_ERROR",
        formData: newInterview,
      });
    }
  }
);

const InterviewSlice = createSlice({
  name: "Interview",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    removeInterview: (state, action) => {
      if (Array.isArray(state.data)) {
        state.data = state.data.filter(
          (interview) => interview.uuid !== action.payload
        );
      }
    },

    resetInterviewState: () => initialState,

    updateInterviewStatusLocally: (state, action) => {
      const { uuid, status } = action.payload;
      if (Array.isArray(state.data)) {
        const index = state.data.findIndex(
          (interview) => interview.uuid === uuid
        );
        if (index !== -1) {
          state.data[index] = {
            ...state.data[index],
            status,
            updatedAt: Date.now(),
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchInterviewsAsync.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchInterviewsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
        state.lastFetch = action.payload.timestamp;
        state.lastQuery = action.payload.query;
        state.error = null;
      })
      .addCase(fetchInterviewsAsync.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error =
          action.payload?.message ||
          action.error?.message ||
          "Failed to fetch interviews";
      })

      // Add
      .addCase(addInterviewAsync.pending, (state) => {
        state.addingInterview = true;
        state.error = null;
      })
      .addCase(addInterviewAsync.fulfilled, (state, action) => {
        state.addingInterview = false;
        state.error = null;

        if (!Array.isArray(state.data)) {
          state.data = [];
        }

        state.data.unshift(action.payload);

        if (state.data.length > 100) {
          state.data = state.data.slice(0, 100);
        }
      })
      .addCase(addInterviewAsync.rejected, (state, action) => {
        state.addingInterview = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          "Failed to schedule interview";
      })

      // Update
      .addCase(updateInterviewAsync.pending, (state, action) => {
        state.updatingInterview = true;
        state.error = null;

        const uuid = action.meta.arg.uuid;
        if (uuid && Array.isArray(state.data)) {
          const index = state.data.findIndex(
            (interview) => interview.uuid === uuid
          );
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.meta.arg.data,
              updating: true,
            };
          }
        }
      })
      .addCase(updateInterviewAsync.fulfilled, (state, action) => {
        state.updatingInterview = false;
        state.error = null;

        const { uuid, data } = action.payload;

        if (Array.isArray(state.data)) {
          const index = state.data.findIndex(
            (interview) => interview.uuid === uuid
          );

          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...data,
              updating: false,
              updatedAt: action.payload.updatedAt,
            };
          } else {
            state.data.unshift({
              ...data,
              updating: false,
              updatedAt: action.payload.updatedAt,
            });
          }
        } else {
          state.data = [
            { ...data, updating: false, updatedAt: action.payload.updatedAt },
          ];
        }
      })
      .addCase(updateInterviewAsync.rejected, (state, action) => {
        state.updatingInterview = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          "Failed to update interview";

        // Revert optimistic update
        const uuid = action.payload?.uuid || action.meta?.arg?.uuid;
        if (uuid && Array.isArray(state.data)) {
          const index = state.data.findIndex(
            (interview) => interview.uuid === uuid
          );
          if (index !== -1) {
            const { updating, ...rest } = state.data[index];
            state.data[index] = rest;
          }
        }
      });
  },
});

// Selectors
export const selectInterview = (state) => state.interview?.data || [];
export const selectInterviewPagination = (state) =>
  state.interview?.pagination || null;
export const selectInterviewLastQuery = (state) =>
  state.interview?.lastQuery || null;
export const selectInterviewLoading = (state) =>
  state.interview?.loading || false;
export const selectInterviewError = (state) => state.interview?.error;
export const selectInterviewStatus = (state) =>
  state.interview?.status || "idle";
export const selectAddingInterview = (state) =>
  state.interview?.addingInterview || false;
export const selectUpdatingInterview = (state) =>
  state.interview?.updatingInterview || false;

export const selectInterviewByUuid =
  (uuid) =>
    (state) => {
      const interviews = selectInterview(state);
      return Array.isArray(interviews)
        ? interviews.find((interview) => interview.uuid === uuid)
        : null;
    };

export const selectInterviewsByStatus =
  (status) =>
    (state) => {
      const interviews = selectInterview(state);
      return Array.isArray(interviews)
        ? interviews.filter((interview) => interview.status === status)
        : [];
    };

export const {
  clearError,
  removeInterview,
  resetInterviewState,
  updateInterviewStatusLocally,
} = InterviewSlice.actions;

export default InterviewSlice.reducer;